import { useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
import useFeaturePermissions from '../../../hooks/useFeaturePermissions'
// UI Components
import Description from '../../ui-helper-elements/Description'
// JS Imports
import { saveOpportunity } from '../../../ts/data'
import { fetchPayrollInfoRecords } from '../../../js/data.js'
import toast from 'react-hot-toast'

interface CloneEinButtonProps {
   buttonClass?: string | undefined
   buttonSize?: 'sm' | 'lg' | undefined
}

export default function CloneEinButton(props: CloneEinButtonProps) {
   // Access the quote context
   const state = useQuoteContext()
   const { getString, getFormattedString } = state
   const { hasPermission } = useFeaturePermissions()

   // State for modal visibility and button confirmation
   const [showModal, setShowModal] = useState(false)
   const [isConfirmMode, setIsConfirmMode] = useState(false)
   const [isProcessing, setIsProcessing] = useState(false)

   // Get permission for CloneEINs feature
   const permissionsCloneEINs = state.featurePermissions['CloneEINs']

   // If user doesn't have permission, don't render the button
   // (must stay below all hooks so the hook count is stable across role changes)
   if (
      !permissionsCloneEINs?.includes(state.user_role) ||
      state.opportunity_id?.startsWith('ACC-')
   ) {
      return null
   }

   // Host URL from environment variable
   const host_url = process.env.REACT_APP_HOST_URL ?? ''

   // Handle modal open/close
   const handleShow = () => setShowModal(true)
   const handleClose = () => {
      setShowModal(false)
      setIsConfirmMode(false) // Reset confirmation state when closing modal
   }

   // Handle form submission
   const handleProceed = async () => {
      if (!isConfirmMode) {
         // First click - switch to confirmation mode
         setIsConfirmMode(true)
      } else {
         // Second click - proceed with saving the opportunity data
         if (isProcessing) return // Prevent multiple submissions

         setIsProcessing(true)
         const toastId = toast.loading(
            `${actionType === 'Update' ? 'Updating' : 'Cloning'} Payroll Info Records...`,
         )

         try {
            // First, save the opportunity data
            const saveResponse = await saveOpportunity({
               state: state,
               hasUnlimitedDatePermission: hasPermission(
                  'UnlimitedActivationDateSelect',
                  state.user_role,
               ),
            })

            // Handle the case where saveResponse might be undefined
            if (saveResponse && !saveResponse.ok) {
               const errorText = await saveResponse.text()
               throw new Error(`Failed to save opportunity: ${errorText}`)
            }

            // Then, send a request to the payroll-info-records endpoint with the appropriate method
            const response = await fetch(`${host_url}/api/payroll-info`, {
               method: httpMethod,
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  opportunity_id: state.opportunity_id,
               }),
            })

            if (!response.ok) {
               const errorText = (await response.json()).message
               handleClose()
               throw new Error(
                  `Failed to ${actionType === 'Update' ? 'update' : 'clone'} Payroll Info Records: ${errorText}`,
               )
            }

            // Success - fetch updated payroll info records and update state
            // Ensure all parameters are strings with null coalescing operators
            const { records } = await fetchPayrollInfoRecords(
               state.opportunity_id ?? '',
               host_url,
            )
            state.setPayrollInfoRecords(records || [])

            // Show success message and close modal
            toast.success(
               getFormattedString('product.CloneEinButton.Toast.SuccessText', {
                  status: isUpdateMode ? 'updated' : 'created',
               }),
               { id: toastId, style: { maxWidth: '60%', width: '500px' } },
            )
            handleClose()
         } catch (error) {
            console.error('Error creating Payroll Info Records:', error)
            toast.error(
               (t) => (
                  <div
                     className='modal show'
                     style={{ display: 'block', position: 'initial' }}
                  >
                     <Modal.Dialog>
                        <Modal.Header
                           closeButton
                           onClick={() => toast.dismiss(t.id)}
                        >
                           <Modal.Title>
                              {getFormattedString(
                                 'product.CloneEinButton.Modal.Title',
                                 {
                                    actionType:
                                       actionType === 'Update'
                                          ? 'Updating'
                                          : 'Cloning',
                                 },
                              )}
                           </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                           <p>
                              {error instanceof Error
                                 ? error.message
                                 : getFormattedString(
                                      'product.CloneEinButton.Toast.Modal.ErrorText',
                                   )}
                           </p>
                        </Modal.Body>
                     </Modal.Dialog>
                  </div>
               ),
               {
                  id: toastId,
                  style: { maxWidth: '60%', width: '500px' },
                  duration: Infinity,
               },
            )
         } finally {
            setIsProcessing(false)
         }
      }
   }

   // When diff is unchecked, all EINs are treated as new regardless of account data.
   const existingEINCount = state.differenceQuote
      ? state.accountSnowflake.EINs__c?.[0]?.length || 0
      : 0
   const newEINsOnQuote = state.productPayrollEINCount - existingEINCount
   const hasNewEINs = state.productPayrollEINCount > existingEINCount

   // Get the list of new EIN names
   const newEINNames =
      state.productPayrollEINNames?.slice(
         existingEINCount,
         state.productPayrollEINCount,
      ) || []

   // Check if all EIN names are valid (non-empty and not just whitespace)
   const allEINNamesValid =
      newEINNames.length > 0 &&
      newEINNames.every(
         (name: string) =>
            name && typeof name === 'string' && name.trim().length > 0,
      )

   // Button should be enabled only if there are new EINs AND all names are valid
   const buttonEnabled = hasNewEINs && allEINNamesValid

   // Determine if we're updating or creating records
   const isUpdateMode =
      state.payrollInfoRecords && state.payrollInfoRecords.length > 0
   const httpMethod = isUpdateMode ? 'PATCH' : 'POST'
   const actionType = isUpdateMode ? 'Update' : 'Clone'

   // Get pluralized form based on count
   const getFormText = (
      count: number,
      singularText: string,
      pluralText: string,
   ): string => (count > 1 ? pluralText : singularText)

   // Determine button text based on action type and count
   const buttonText = isUpdateMode
      ? getFormText(
           state.payrollInfoRecords.length,
           'Update Payroll Info',
           'Update Payroll Infos',
        )
      : getFormText(
           newEINsOnQuote,
           'Create Payroll Info',
           'Create Payroll Infos',
        )

   // Determine the reason why the button is disabled
   let disabledReason = ''
   if (!hasNewEINs) {
      disabledReason = 'No new Payroll Info records to create'
   } else if (!allEINNamesValid) {
      disabledReason = 'All EINs must have valid names'
   }

   // Button component to be conditionally wrapped
   const buttonComponent = (
      <Button
         onClick={handleShow}
         className={props.buttonClass}
         size={props.buttonSize}
         disabled={!buttonEnabled}
      >
         {buttonText}
      </Button>
   )

   return (
      <>
         {!buttonEnabled ? (
            <Description
               content={disabledReason}
               placement='top'
               popoverClassName='warning-popover'
               className='width-text'
               title='Button Disabled'
            >
               {buttonComponent}
            </Description>
         ) : (
            buttonComponent
         )}

         <Modal show={showModal} onHide={handleClose} centered={true} size='lg'>
            <Modal.Header>
               <Modal.Title className='w-100 text-center'>
                  {actionType} EIN
                  {newEINsOnQuote > 1 ||
                  (isUpdateMode && state.payrollInfoRecords.length > 1)
                     ? 's'
                     : ''}
               </Modal.Title>
            </Modal.Header>
            <Form noValidate>
               <Modal.Body>
                  <p>
                     {isUpdateMode
                        ? getString('product.CloneEinButton.Modal.UpdateText')
                        : getFormattedString(
                             'product.CloneEinButton.Modal.CreateText',
                             {
                                additionalText:
                                   newEINsOnQuote > 1
                                      ? getFormattedString(
                                           'product.CloneEinButton.Modal.CreateText.PluralText',
                                           { newEINsOnQuote: newEINsOnQuote },
                                        )
                                      : getString(
                                           'product.CloneEinButton.Modal.CreateText.SingularText',
                                        ),
                             },
                          )}
                  </p>
                  {isUpdateMode &&
                     state.payrollInfoRecords &&
                     state.payrollInfoRecords.length > 0 && (
                        <div className='mt-3'>
                           <div
                              dangerouslySetInnerHTML={{
                                 __html: getString(
                                    'product.CloneEinButton.Modal.UpdateModeText',
                                 ),
                              }}
                           />
                           <ul>
                              {state.productPayrollEINNames
                                 .slice(existingEINCount)
                                 .map((einName: string, index: number) => (
                                    <li key={`ein-update-${index}`}>
                                       {einName}
                                    </li>
                                 ))}
                           </ul>
                        </div>
                     )}
                  {!isUpdateMode && newEINNames.length > 0 && (
                     <div className='mt-3'>
                        <div
                           dangerouslySetInnerHTML={{
                              __html: getString(
                                 'product.CloneEinButton.Modal.CreateModeText',
                              ),
                           }}
                        />
                        <ul>
                           {newEINNames.map(
                              (einName: string, index: number) => (
                                 <li key={`ein-${index}`}>{einName}</li>
                              ),
                           )}
                        </ul>
                     </div>
                  )}
               </Modal.Body>
               <Modal.Footer className='flex-column align-items-end'>
                  <div className='d-flex'>
                     <Button
                        variant='secondary'
                        onClick={handleClose}
                        disabled={isProcessing}
                     >
                        {getString('global.Cancel')}
                     </Button>
                     <Button
                        variant={isConfirmMode ? 'warning' : 'primary'}
                        onClick={handleProceed}
                        disabled={isProcessing}
                        className='ms-2'
                     >
                        {isProcessing
                           ? getString('global.Processing')
                           : isConfirmMode
                             ? getString('global.AreYouSure')
                             : getString('global.Proceed')}
                     </Button>
                  </div>
               </Modal.Footer>
            </Form>
         </Modal>
      </>
   )
}
